/**
 * What a proposal's change says on the card (boards S, S3): its kind, its title, the line before
 * and the line after. `t` and the locale arrive as parameters; the facts are `proposalFacts`'.
 *
 * An exercise's prescription reads as the canon writes it: „4 × 6 @RPE 8 · +2,5 kg / cykl".
 */

import type { TFunction } from 'i18next';

import { prescriptionLine, type AiProposalChangeFacts, type AiProposalDay, type ExerciseDraft, type PrescriptionLine } from '~/entities/plan-draft';
import { numberFormat } from '~/shared/lib/intlCache';

import type { ExerciseDrawing } from '../model/exerciseDrawing';
import { alterKind, prescriptionDiff } from '../model/prescriptionDiff';
import type { ProposalHeading } from '../model/proposalCard';

import { FACT_SEPARATOR } from './planWording';

const KEY = 'web:aiPlan.proposal';

const VALUE_UNIT = { reps: null, duration: 'seconds', distance: 'metres', rounds: 'rounds' } as const;

const DELTA_UNIT: Readonly<Record<string, string>> = {
  weight: 'kg',
  reps: 'reps',
  duration: 'seconds',
  distance: 'metres',
  rounds: 'rounds',
  rpe: 'points',
  rir: 'points',
};

/** What a line states beyond its sets and their size: the parts a change moved are shown. */
export interface LineParts {
  readonly load: boolean;
  readonly rest: boolean;
}

const ALL_PARTS: LineParts = { load: true, rest: false };

/** Values set by set: one value when they agree, else each in order („10/8/6"). */
function valuesWords(values: readonly number[], format: (value: number) => string): string {
  const distinct = [...new Set(values)];
  return distinct.length === 1 ? format(distinct[0] ?? 0) : values.map(format).join('/');
}

/**
 * „rozgrzewka 1 + 4 × 6 · 60 kg @RPE 8 · +2,5 kg / cykl", or as much of it as the exercise
 * prescribes. Every set is described (a warm-up exercise reads „2 × 15"); the load shows by default,
 * the rest only where a change moved it.
 */
export function prescriptionWords(t: TFunction, locale: string, line: PrescriptionLine, parts: LineParts = ALL_PARTS): string {
  const number = numberFormat(locale, { maximumFractionDigits: 2 });
  const words: string[] = [];
  if (line.warmups > 0) words.push(t(`${KEY}.line.warmups`, { count: line.warmups }));
  if (line.volume !== null) {
    const unit = VALUE_UNIT[line.volume.field];
    const value = valuesWords(line.volume.values, (each) => number.format(each));
    words.push(t(`${KEY}.line.sets`, { sets: line.sets, value: unit === null ? value : t(`${KEY}.unit.${unit}`, { value }) }));
  } else {
    words.push(t(`${KEY}.line.setsOnly`, { count: line.sets }));
  }
  let head = words.join(' ');
  if (parts.load && line.load !== null) {
    const load = line.load;
    const value =
      load.field === 'percent'
        ? t(`${KEY}.unit.percent`, { value: valuesWords(load.values, (each) => number.format(each * 100)) })
        : t(`${KEY}.unit.kg`, { value: valuesWords(load.values, (each) => number.format(each)) });
    head = `${head}${FACT_SEPARATOR}${value}`;
  }
  if (line.effort !== null) {
    const axis = line.effort.field;
    head = `${head} ${t(`${KEY}.line.${axis}`, { value: valuesWords(line.effort.values, (each) => number.format(each)) })}`;
  }
  if (parts.rest && line.rest !== null) {
    head = `${head}${FACT_SEPARATOR}${t(`${KEY}.line.rest`, { value: valuesWords(line.rest.values, restWords) })}`;
  }
  if (line.progression === null) return head;
  return `${head}${FACT_SEPARATOR}${progressionWords(t, locale, line.progression)}`;
}

/** „2:00". */
function restWords(seconds: number): string {
  return `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, '0')}`;
}

/** „+2,5 kg / cykl", „+5% / cykl", „+2 powt. / cykl". */
function progressionWords(t: TFunction, locale: string, progression: NonNullable<PrescriptionLine['progression']>): string {
  const delta = numberFormat(locale, { maximumFractionDigits: 2, signDisplay: 'always' }).format(progression.delta);
  const unit = progression.unit === 'Percent' ? 'percent' : (DELTA_UNIT[progression.field] ?? 'points');
  return t(`${KEY}.line.perCycle`, { delta: t(`${KEY}.unit.${unit}`, { value: delta }) });
}

/** A day as the card names it: its own name, else its place in the cycle. */
export function dayWords(t: TFunction, day: AiProposalDay): string {
  const name = day.name?.trim() ?? '';
  return name === '' ? t(`${KEY}.dayAt`, { position: day.position }) : name;
}

export function headingWords(t: TFunction, heading: ProposalHeading): string {
  switch (heading.kind) {
    case 'day':
      return dayWords(t, heading.day);
    case 'newDay':
      return t(`${KEY}.group.newDay`);
    case 'structure':
      return t(`${KEY}.group.structure`);
  }
}

export interface ChangeWords {
  readonly kind: string;
  readonly title: string;
  /** What the change replaces, drawn struck through; `null` when it replaces nothing. */
  readonly before: string | null;
  /** What it becomes; `null` when it is a removal. */
  readonly after: string | null;
  /** The sentence an outdated change carries instead of its reason. */
  readonly outdated: string;
}

export function changeWords(t: TFunction, locale: string, facts: AiProposalChangeFacts): ChangeWords {
  const { change, before, after } = facts;
  const line = (exercise: typeof before) => (exercise === null ? null : prescriptionWords(t, locale, prescriptionLine(exercise)));
  const cycles = (count: number) => t('web:aiPlan.cycles', { count });
  const outdatedExercise = t(`${KEY}.outdatedExercise`);
  switch (change.kind) {
    case 'exerciseReplace':
      return {
        kind: t(`${KEY}.kind.exerciseReplace`),
        title: after?.name ?? '',
        before: before === null ? null : `${before.name} ${line(before) ?? ''}`.trim(),
        after: line(after),
        outdated: outdatedExercise,
      };
    case 'exerciseAlter': {
      if (before === null || after === null) {
        return { kind: t(`${KEY}.kind.exerciseSets`), title: after?.name ?? before?.name ?? '', before: null, after: line(after), outdated: outdatedExercise };
      }
      const diff = prescriptionDiff(before, after);
      const kind = alterKind(diff);
      const parts: LineParts = { load: true, rest: diff.rest };
      const was = prescriptionLine(before);
      const becomes = prescriptionLine(after);
      if (kind === 'progression') {
        // Only the rule moved: the sets that stay, then the rule as it is now.
        return {
          kind: t(`${KEY}.kind.exerciseProgression`),
          title: after.name,
          before: null,
          after: `${prescriptionWords(t, locale, { ...becomes, progression: null }, parts)} → ${
            becomes.progression === null ? t(`${KEY}.line.noProgression`) : progressionWords(t, locale, becomes.progression)
          }`,
          outdated: outdatedExercise,
        };
      }
      const beforeWords = prescriptionWords(t, locale, was, parts);
      const afterWords = prescriptionWords(t, locale, becomes, parts);
      return {
        kind: t(`${KEY}.kind.${kind === 'sets' ? 'exerciseSets' : 'exerciseOther'}`),
        title: after.name,
        before: kind === 'sets' && beforeWords !== afterWords ? beforeWords : null,
        // A change the line cannot show (a tempo, a set's type) says so rather than repeating itself.
        after: kind === 'sets' && beforeWords === afterWords ? `${afterWords}${FACT_SEPARATOR}${t(`${KEY}.line.otherSetValues`)}` : afterWords,
        outdated: outdatedExercise,
      };
    }
    case 'exerciseAdd':
      return {
        kind:
          facts.day?.added === true
            ? t(`${KEY}.kind.exerciseAddOnDay`, { day: dayWords(t, facts.day) })
            : t(`${KEY}.kind.exerciseAdd`),
        title: after?.name ?? '',
        before: null,
        after: line(after),
        outdated: t(`${KEY}.outdatedDay`),
      };
    case 'exerciseRemove':
      return { kind: t(`${KEY}.kind.exerciseRemove`), title: before?.name ?? '', before: line(before), after: null, outdated: outdatedExercise };
    case 'dayAdd':
      return {
        kind: t(`${KEY}.kind.dayAdd`),
        title: facts.day === null ? '' : dayWords(t, facts.day),
        before: null,
        after: facts.follows === null ? t(`${KEY}.dayFirst`) : t(`${KEY}.dayAfter`, { day: dayWords(t, facts.follows) }),
        outdated: t(`${KEY}.outdatedPhase`),
      };
    case 'dayRemove':
      return {
        kind: t(`${KEY}.kind.dayRemove`),
        title: facts.day === null ? '' : dayWords(t, facts.day),
        before: null,
        after: null,
        outdated: t(`${KEY}.outdatedDay`),
      };
    case 'phaseAdd': {
      const between = facts.between;
      const where =
        between === null
          ? null
          : between.after !== null && between.before !== null
            ? t(`${KEY}.phaseBetween`, { after: between.after, before: between.before })
            : between.after !== null
              ? t(`${KEY}.phaseAfter`, { after: between.after })
              : between.before !== null
                ? t(`${KEY}.phaseBefore`, { before: between.before })
                : null;
      return {
        kind: t(`${KEY}.kind.phaseAdd`),
        title: facts.phase === null ? '' : `${facts.phase.name}${FACT_SEPARATOR}${cycles(facts.phase.cycles)}`,
        before: null,
        after: where,
        outdated: t(`${KEY}.outdatedPhase`),
      };
    }
    case 'phaseRemove':
      return {
        kind: t(`${KEY}.kind.phaseRemove`),
        title: facts.phase?.name ?? '',
        before: facts.phase === null ? null : cycles(facts.phase.cycles),
        after: null,
        outdated: t(`${KEY}.outdatedPhase`),
      };
    case 'phaseCycles':
      return {
        kind: t(`${KEY}.kind.phaseCycles`),
        title: facts.phase?.name ?? '',
        before: facts.phase === null ? null : cycles(facts.phase.cycles),
        after: cycles(change.cycleCount),
        outdated: t(`${KEY}.outdatedPhase`),
      };
  }
}

/** What a change drawn as the builder's exercise card says around the card (board U). */
export interface ExerciseCardWords {
  /** The head's right end: „4 → 5 serii", „Nowe w miejscu 2", „Nowe, na końcu dnia"; `null` for none. */
  readonly tag: string | null;
  /** A replacement's old exercise, said struck above the card („Wiosłowanie hantlem · 4 × 8"). */
  readonly replaced: string | null;
  /** The increment each rule states („+2,5 kg / cykl"): what the card's progression row adds. */
  readonly increments: { readonly before: string | null; readonly after: string | null };
}

function incrementWords(t: TFunction, locale: string, exercise: ExerciseDraft | null): string | null {
  const progression = exercise === null ? null : prescriptionLine(exercise).progression;
  return progression === null ? null : progressionWords(t, locale, progression);
}

export function exerciseCardWords(t: TFunction, locale: string, drawing: ExerciseDrawing): ExerciseCardWords {
  const { baseline, exercise, ordinal, replaced } = drawing;
  const previous = baseline.kind === 'previous' ? baseline.exercise : null;
  const tag =
    baseline.kind === 'added'
      ? t(`${KEY}.card.atEnd`)
      : baseline.kind === 'none'
        ? ordinal === null
          ? null
          : t(`${KEY}.card.atPosition`, { position: ordinal })
        : previous !== null && previous.sets.length !== exercise.sets.length
          ? t(`${KEY}.card.setCount`, { before: previous.sets.length, count: exercise.sets.length })
          : null;
  // The old exercise in one line: its sets and their size, as the week card reads it.
  const short = (line: PrescriptionLine) =>
    prescriptionWords(t, locale, { ...line, effort: null, progression: null }, { load: false, rest: false });
  return {
    tag,
    replaced: replaced === null ? null : t(`${KEY}.card.wasLine`, { name: replaced.name, line: short(prescriptionLine(replaced)) }),
    increments: { before: incrementWords(t, locale, previous), after: incrementWords(t, locale, exercise) },
  };
}
